import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { useCurrency } from '@/lib/platform/contexts/currency-context';
import { getCurrencySymbol } from '@/lib/platform/utils/currency';
import { NumberInput, Select } from '@/components/platform/forms';
import Tooltip from '@/components/platform/ui/Tooltip';
import { tooltipData } from '@/lib/platform/data/tooltip-data';
import type { NumberFormatType } from '@/lib/platform/utils/locale-formatting';
import { AddEntryButton } from './AddEntryButton';
import { useBlankStart } from './use-blank-start';

interface TVSectionProps {
  tvDuration: number;
  tvSizeCategory: string;
  tvCpm: number;
  tvViewers: number;
  onTvDurationChange: (value: number) => void;
  onTvSizeCategoryChange: (value: string) => void;
  onTvCpmChange: (value: number) => void;
  onTvViewersChange: (value: number) => void;
  canEdit: boolean;
  numberFormat?: NumberFormatType;
}

export function TVSection({
  tvDuration,
  tvSizeCategory,
  tvCpm,
  tvViewers,
  onTvDurationChange,
  onTvSizeCategoryChange,
  onTvCpmChange,
  onTvViewersChange,
  canEdit,
  numberFormat = 'eu',
}: TVSectionProps) {
  const { darkMode } = useDarkMode();
  const { baseCurrency } = useCurrency();
  const [expanded, expand] = useBlankStart(
    tvDuration !== 0 || tvCpm !== 0 || tvViewers !== 0 || tvSizeCategory !== 'M'
  );

  return (
    <div
      className={`grid grid-cols-1 gap-x-8 gap-y-10 border-b pb-12 md:grid-cols-3 ${darkMode ? 'border-white/10' : 'border-gray-900/10'} ${!canEdit ? 'opacity-70' : ''}`}
    >
      <div>
        <h2 className={`text-base/7 font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
          TV
          <span
            className={`ml-2 rounded px-2 py-1 text-xs font-medium ${
              darkMode ? 'bg-white/10 text-gray-300' : 'bg-black/6 text-gray-600'
            }`}
          >
            HQ
          </span>
        </h2>
        <p className={`mt-1 text-sm/6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Television broadcast duration, size, and viewership metrics.
        </p>
        {!canEdit && (
          <span className="mt-2 inline-block rounded bg-red-500/10 px-2 py-1 text-xs font-medium text-red-500">
            Read Only
          </span>
        )}
      </div>

      {!expanded && (
        <div className="max-w-2xl md:col-span-2">
          <AddEntryButton label="TV" onClick={expand} disabled={!canEdit} />
        </div>
      )}

      {expanded && (
        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
          {/* TV Duration */}
          <div className="sm:col-span-3">
            <NumberInput
              label={
                <>
                  TV Duration (minutes)
                  <Tooltip {...tooltipData.tvDuration} wide={true} />
                </>
              }
              value={tvDuration}
              onChange={(value) => onTvDurationChange(typeof value === 'number' ? value : 0)}
              decimals={0}
              numberFormat={numberFormat}
              placeholder="0"
              disabled={!canEdit}
            />
          </div>

          {/* TV Size Category */}
          <div className="sm:col-span-3">
            <Select
              label={
                <>
                  TV Size Category
                  <Tooltip {...tooltipData.tvSize} wide={true} />
                </>
              }
              value={tvSizeCategory}
              onChange={(value) => onTvSizeCategoryChange(value as string)}
              options={[
                { value: 'S', label: 'Small (≤1%)' },
                { value: 'M', label: 'Medium (1-5%)' },
                { value: 'L', label: 'Large (5-15%)' },
                { value: 'XL', label: 'Extra Large (>15%)' },
              ]}
              disabled={!canEdit}
            />
          </div>

          {/* TV CPM */}
          <div className="sm:col-span-3">
            <NumberInput
              label={
                <>
                  TV CPM
                  <Tooltip {...tooltipData.tvCpm} wide={true} />
                </>
              }
              value={tvCpm}
              onChange={(value) => onTvCpmChange(typeof value === 'number' ? value : 0)}
              decimals={2}
              numberFormat={numberFormat}
              suffix={getCurrencySymbol(baseCurrency)}
              placeholder="0"
              disabled={!canEdit}
            />
          </div>

          {/* TV Viewers */}
          <div className="sm:col-span-3">
            <NumberInput
              label={
                <>
                  TV Viewers
                  <Tooltip {...tooltipData.tvViewers} wide={true} />
                </>
              }
              value={tvViewers}
              onChange={(value) => onTvViewersChange(typeof value === 'number' ? value : 0)}
              decimals={0}
              numberFormat={numberFormat}
              placeholder="0"
              disabled={!canEdit}
            />
          </div>
        </div>
      )}
    </div>
  );
}
